import {describeCurrentStep, isStepSettled, notePasskeyPrompt} from "@/functions/webMcpStep";
import {cleanText, isSecretInput, isVisible, labelFor, snapshotPage, waitForOutcome} from "@/functions/webMcpDom";

const SECRET_REASONS = {
	password: 'Passwords never pass through a tool or this conversation, so don\'t ask for one. The person types it into the page, or their password manager, browser autofill or your secure sign-in fills it.',
	code: 'Codes never pass through a tool or this conversation, so don\'t ask for one. The person types the code they received into the page, or the browser fills it from the text message.',
}

const PASSKEY_PROMPT = 'The browser is showing a passkey prompt. Only the person can approve it with their fingerprint, face, or device PIN. Ask them to, then call get_auth_state.'

const settleOutcome = (options) => waitForOutcome({describeStep: describeCurrentStep, isSettled: isStepSettled, ...options})

export const waitForSecrets = (toolName, selectors) => {
	const empty = selectors.map((target) => (typeof target === 'string' ? document.querySelector(target) : target)).filter((element) => element && isVisible(element) && element.value === '')
	if (!empty.length) return null
	empty[0].focus()
	const waitingFor = empty.some((element) => element.type === 'password' || element.id === 'username') ? 'password' : 'code'
	return {result: {
		outcome: 'waiting_for_person',
		waitingFor,
		fields: empty.map(labelFor),
		reason: `${SECRET_REASONS[waitingFor]} Once it is filled, call ${toolName} again and it presses the button; if the person pressed it themselves, call get_auth_state.`,
		step: describeCurrentStep(),
	}}
}

const invalidNonSecretFields = (form) => [...(form?.elements ?? [])]
	.filter((element) => element.willValidate && !isSecretInput(element) && !element.checkValidity())
	.map((element) => ({field: element.id || labelFor(element), problem: element.validationMessage || 'This field is required.'}))

export const requireFilled = (input, fields) => {
	const invalid = Object.entries(fields)
		.filter(([key]) => !String(input[key] ?? '').trim())
		.map(([key, problem]) => ({field: key, problem}))
	return invalid.length ? {invalid} : null
}

export const readyForPerson = (target, {secrets = [], reason}) => {
	const button = typeof target === 'string' ? document.querySelector(target) : target
	if (!button || !isVisible(button)) {
		return {result: {error: 'The button the person needs to press is not showing. Call get_auth_state.', errorCode: 'PAGE_CHANGED', retryable: true}}
	}
	const invalid = button.form && !button.form.noValidate ? invalidNonSecretFields(button.form) : []
	if (invalid.length) return {invalid}
	const label = cleanText(button)
	const empty = secrets.map((secret) => (typeof secret === 'string' ? document.querySelector(secret) : secret)).filter((element) => element && isVisible(element) && element.value === '')
	const focusTarget = empty[0] ?? button
	focusTarget.focus()
	return {result: {
		outcome: 'ready_for_person',
		button: label,
		personEnters: empty.length ? empty.map(labelFor) : undefined,
		reason: empty.length
			? `The person types ${empty.map(labelFor).join(' and ')} into the page, or their password manager or your secure sign-in fills it, then presses "${label}" themselves: ${reason} After that, call get_auth_state.`
			: `The person presses "${label}" themselves: ${reason} After that, call get_auth_state.`,
		step: describeCurrentStep(),
	}}
}

export const noInput = {type: 'object', properties: {}, additionalProperties: false}

export const stringInput = (description, extra = {}) => ({type: 'string', description, ...extra})

export const objectInput = (properties, required = []) => ({type: 'object', properties, required, additionalProperties: false})

export const readOnlyTool = ({name, description, inputSchema = noInput, untrusted = false, execute}) => ({
	name,
	description,
	inputSchema,
	annotations: {readOnlyHint: true, ...(untrusted ? {untrustedContentHint: true} : {})},
	execute,
})

export const navigationTool = ({name, description, inputSchema = noInput, execute}) => ({
	name,
	description,
	inputSchema,
	annotations: {untrustedContentHint: true},
	execute: async (input, context) => {
		const start = snapshotPage()
		const handled = await execute(input, context)
		if (handled?.result) return handled.result
		return settleOutcome({start, signal: context?.signal})
	},
})

export const actionTool = ({name, description, inputSchema = noInput, run, startsPasskeyPrompt = false}) => ({
	name,
	description,
	inputSchema,
	isAction: true,
	annotations: {consequentialHint: true, untrustedContentHint: true},
	execute: async (input, context) => {
		if (context?.signal?.aborted) return {outcome: 'cancelled', step: describeCurrentStep()}
		const start = snapshotPage()
		const pressed = await run(input)
		if (pressed?.invalid?.length) {
			return {outcome: 'invalid_fields', invalid: pressed.invalid, step: describeCurrentStep()}
		}
		if (pressed?.result) return pressed.result
		if (startsPasskeyPrompt) {
			notePasskeyPrompt()
			return {outcome: 'waiting_for_person', waitingFor: 'passkey', reason: PASSKEY_PROMPT, step: describeCurrentStep()}
		}
		return settleOutcome({start, signal: context?.signal})
	},
})